import { useCallback, useEffect, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import pluralize from 'pluralize';
import { saveAs } from 'file-saver';

import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalActions,
  SimpleModalContentInset,
  SimpleModalContentOuter,
} from 'lib/modal/simple-modal-scrollable.style';
import { SecondaryButton } from 'approot/shared/styles/buttons/buttons';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import {
  getSectionsAndSubSections,
  getSectionsAndSubSectionsOverview,
} from 'approot/activity-builder/activity-builder.utils';
import SimpleCardLabel from 'approot/shared/simple-card/simple-card-label';
import {
  ACTIVITY_BUILDER_SLIDE_RATIO,
  SectionOrSubSectionOverview,
} from 'approot/activity-builder/activity-builder.constants';
import {
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ActivitiesContentType,
  INTRODUCTION_SECTION_ID,
  LANGUAGE_CODE_EN_AU,
  LanguageCodes,
} from 'global/constants';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import CheckableCard from 'approot/shared/worksheet-card/checkbox-worksheet-card/checkable-card';
import { ActivityPagePreview } from 'approot/activity-builder/page/activity-builder-page-preview';
import ActivityBuilderAnswersPagePreview from 'approot/activity-builder/page/activity-builder-answers-page-preview';
import { ActivityBuilderThemeContextProvider } from 'approot/activity-builder/theme/activity-builder-theme';
import { Heading20, SmallStyled } from 'approot/shared/styles/typography.style';
import { PublishedLessonActivities } from '../graphql/get-published-lesson-activities.constants';
import { PublishedAssessmentActivities } from '../graphql/get-published-assessment-activities.constants';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useCurrentClassVar } from 'approot/classes/classes.hooks';
import { useGetClassStudents } from '../classroom/classroom-student.hooks';
import { useGetActivitySections } from '../activity-builder-section.hooks';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { formatToLowerKebabCase } from 'approot/shared/logic/format-to-lower-kebab-case';
import { getComponentsOfType } from '../components/shared/logic/get-components-of-type';
import { ComponentTable } from '../components/component.constants';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import type {
  GetClassStudentAnswersQuery,
  GetClassStudentAnswersQueryVariables,
} from '../present/activity-answers.graphql.generated';
import { GET_CLASS_STUDENT_ANSWERS } from '../present/activity-answers.graphql';
import TeacherDownloadStudentAnswersOptions from './teacher-download-student-answers-options';
import { DownloadAnswerFormat } from './teacher-download-student-answer.constants';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { Student } from '../classroom/classroom.constants';
import { getPrintMetadata } from '../print/answers/logic/get-print-metadata';
import Feedback from 'approot/shared/feedback/feedback';
import * as Styled from './teacher-download-student-answers.style';
import ActivityAnswersPrintIntro from '../print/answers/activity-answers-print-intro';
import { postAuthorised } from 'approot/shared/api/auth/auth.http';
import {
  DraftAssessmentActivities,
  DraftLessonActivities,
} from '../activity-builder-draft.constants';
import { downloadStudentPortfolioPdf } from './teacher-download-student-answers-pdfs';
import {
  AnswerComponent,
  AnswerComponents,
} from '../activity-answer-components.constants';
import { useGetActivityMetadata } from '../logic/use-get-activity-details';
import { FEEDBACK_ASSIGNMENTS_DOWNLOAD } from 'global/constants';
import {
  getStudentIdsWithAnswersSet,
  getSplitStudentsByAnswers,
  getNoStudentsSelected,
  getNoStudentsWithAnswers,
} from './teacher-download-student-answers.utils';

type Props = {
  activities:
    | PublishedLessonActivities
    | PublishedAssessmentActivities
    | DraftLessonActivities
    | DraftAssessmentActivities;
  version: string | null;
  contentId: number;
  contentType: ActivitiesContentType;
  language: LanguageCodes;
  handleClose: () => void;
};

const TeacherDownloadStudentAnswers = ({
  activities,
  version,
  contentId,
  contentType,
  language = LANGUAGE_CODE_EN_AU,
  handleClose,
}: Props) => {
  const currentClass = useCurrentClassVar();
  const classId = currentClass?.id;

  const [format, setFormat] = useState<DownloadAnswerFormat>(
    DownloadAnswerFormat.Pdf
  );
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [selectedStudents, setSelectedStudents] = useState<Student[]>([]);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);

  const { showSuccess, showError } = useSnackbar();
  const isMediaUpSm = useMediaQuery(MEDIA_SM);

  const { data: answersData } = useLoggingQuery<
    GetClassStudentAnswersQuery,
    GetClassStudentAnswersQueryVariables
  >(GET_CLASS_STUDENT_ANSWERS, {
    fetchPolicy: 'network-only',
    variables: {
      input: {
        classId: classId!,
        contentId,
        contentType,
        contentVersion: version!,
        loadDrawingData: false,
      },
    },
  });

  const { data: studentsData, loading: studentsLoading } = useGetClassStudents({
    classId: currentClass?.id || null,
    fetchPolicy: 'cache-and-network',
  });

  const students = useMemo(() => {
    return (
      studentsData?.getClassStudents.map(s => ({
        id: s.id,
        email: s.email,
        username: s.username,
        firstName: s.firstName,
        lastName: s.lastName,
        emeji: s.emeji,
      })) || []
    );
  }, [studentsData]);

  const studentIdsWithAnswersSet = useMemo(
    () => getStudentIdsWithAnswersSet(answersData?.getClassStudentsAnswers),
    [answersData?.getClassStudentsAnswers]
  );

  const { studentsWithAnswers, studentsWithoutAnswers } = useMemo(
    () => getSplitStudentsByAnswers(students, studentIdsWithAnswersSet),
    [students, studentIdsWithAnswersSet]
  );

  const noStudentsSelected = useMemo(
    () => getNoStudentsSelected(selectedStudents),
    [selectedStudents]
  );

  const noStudentsWithAnswers = useMemo(
    () => getNoStudentsWithAnswers(studentsWithAnswers),
    [studentsWithAnswers]
  );

  // Default to all students with answers when PDF format is selected
  useEffect(() => {
    if (format === DownloadAnswerFormat.Pdf) {
      setSelectedStudents(studentsWithAnswers);
    }
  }, [format, studentsWithAnswers]);

  const sectionOverviews = useMemo(() => {
    return activities.sectionsOverview;
  }, [activities]);

  const sortedSectionsOverview = useMemo(
    () => sortBy(sectionOverviews, 'sort'),
    [sectionOverviews]
  );

  const { getMetaData } = useGetActivityMetadata({
    contentType,
    sectionsOverview: sortedSectionsOverview,
  });

  const { data: sectionData, loading: sectionDataLoading } =
    useGetActivitySections({
      classId,
      contentId,
      contentType,
      version,
      isPublic: false,
      sectionIds: sectionOverviews.map(s => s.id),
    });

  const sectionsAndSubSectionsOverview = useMemo(() => {
    const sectionsWithResponses = getSectionsAndSubSections(
      sectionData?.getActivitiesSections || []
    ).filter(section => {
      const components = section.components;
      const responseComponents = getComponentsOfType<AnswerComponent>({
        components,
        types: AnswerComponents,
      }).filter(component => {
        if (
          component.type === ACTIVITIES_COMPONENT_TYPE_TABLE &&
          (component as ComponentTable).properties.table.isReadOnly
        ) {
          return false;
        }
        return true;
      });
      return responseComponents.length > 0;
    });

    const sections = getSectionsAndSubSectionsOverview(sortedSectionsOverview);
    const filteredSections = sections.filter(s =>
      sectionsWithResponses.find(r => r.id === s.id)
    );
    setSelectedPages([
      INTRODUCTION_SECTION_ID,
      ...filteredSections.map(s => s.id),
    ]);

    return filteredSections;
  }, [sortedSectionsOverview, sectionData?.getActivitiesSections]);

  const allSelectedValue = useMemo(
    () => [
      INTRODUCTION_SECTION_ID,
      ...sectionsAndSubSectionsOverview.map(s => s.id),
    ],
    [sectionsAndSubSectionsOverview]
  );

  const totalPages = sectionsAndSubSectionsOverview.length + 1;

  const printMetaData = useMemo(
    () =>
      getPrintMetadata({
        contentType,
        lesson:
          activities.__typename === 'LessonActivities'
            ? {
                name: activities.lesson.name,
                focusQuestion: activities.focusQuestion,
                phenomena: activities.phenomena,
                unit: activities.lesson.unit,
                subjects: activities.lesson.subjects,
              }
            : null,
        assessment:
          activities.__typename === 'AssessmentActivities'
            ? {
                name: activities.assessment.name,
                unit: activities.assessment.unit,
                subjects: activities.assessment.subjects,
              }
            : null,
        landscapeImageUrls: activities.landscapeImage?.responsive || [],
        className: currentClass?.name || '',
      }),
    [activities, currentClass, contentType]
  );

  const handleSelectAll = useCallback(() => {
    setSelectedPages(allSelectedValue);
  }, [allSelectedValue, setSelectedPages]);

  const handleDeselectAll = useCallback(() => {
    setSelectedPages([]);
  }, [setSelectedPages]);

  const ratio = ACTIVITY_BUILDER_SLIDE_RATIO * 100;

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    if (format === DownloadAnswerFormat.Pdf) {
      // Call portfolio handler to download PDFs
      await downloadStudentPortfolioPdf({
        classId,
        className: currentClass?.name || '',
        contentId,
        sectionIds: selectedPages,
        selectedStudents,
        showSuccess,
        showError,
        handleClose,
        setIsDownloading,
      });
    } else {
      const response = await postAuthorised({
        path: '/api/assignments/export-answer-summary',
        postJSON: {
          classId,
          contentVersion: version,
          contentId,
          contentType,
          sectionIds: selectedPages.filter(p => p !== INTRODUCTION_SECTION_ID),
          studentIds: students.map(s => s.id),
        },
      });

      if (response.status !== 200 || !response.body) {
        showError(
          'There was an error preparing the download. Please try again'
        );
      } else {
        // Read the stream and convert to a Blob
        const file = response.blob && (await response.blob());
        if (!file || !file.size) {
          showError(
            'There was an error preparing the download. Please try again'
          );
        }

        saveAs(
          file,
          formatToLowerKebabCase(
            `${
              activities.__typename === 'LessonActivities'
                ? activities.lesson.name
                : activities.assessment.name
            }-student-answers.csv`
          )
        );

        handleClose();
      }
      setIsDownloading(false);
    }
  }, [
    format,
    classId,
    students,
    selectedPages,
    selectedStudents,
    activities,
    contentType,
    contentId,
    version,
    currentClass,
    showSuccess,
    showError,
    setIsDownloading,
    handleClose,
  ]);

  const getTitleDetails = useCallback(
    (section: SectionOrSubSectionOverview) => {
      return getMetaData(section);
    },
    [getMetaData]
  );

  return (
    <SimpleModal
      isVisible
      scrollWithinModal
      isOverflowable
      size={900}
      title="Student Responses"
      headerTitle="Student Responses"
      dataTrackingLabel="download-student-answers"
      onClose={handleClose}
    >
      {sectionDataLoading || studentsLoading ? (
        <SimpleModalContentOuter>
          <SimpleModalContentInset>
            <Bouncer />
          </SimpleModalContentInset>
        </SimpleModalContentOuter>
      ) : (
        <SimpleModalContentOuter>
          {isMediaUpSm && (
            <Styled.TeacherDownloadOptions>
              <TeacherDownloadStudentAnswersOptions
                format={format}
                selectedStudents={selectedStudents}
                allStudents={students}
                noSelectedStudents={noStudentsSelected}
                noStudentsWithAnswers={noStudentsWithAnswers}
                isDownloading={isDownloading}
                setFormat={setFormat}
                setSelectedStudents={setSelectedStudents}
                studentsWithAnswers={studentsWithAnswers || ([] as Student[])}
                studentsWithoutAnswers={studentsWithoutAnswers}
              />
            </Styled.TeacherDownloadOptions>
          )}

          <Styled.TeacherDownloadContent>
            <SimpleModalContentInset>
              {!isMediaUpSm && (
                <TeacherDownloadStudentAnswersOptions
                  format={format}
                  selectedStudents={selectedStudents}
                  allStudents={students}
                  noSelectedStudents={noStudentsSelected}
                  noStudentsWithAnswers={noStudentsWithAnswers}
                  isDownloading={isDownloading}
                  setFormat={setFormat}
                  setSelectedStudents={setSelectedStudents}
                  studentsWithAnswers={studentsWithAnswers || ([] as Student[])}
                  studentsWithoutAnswers={studentsWithoutAnswers}
                />
              )}
              <ActivityBuilderThemeContextProvider
                initialFontSize={activities?.styles?.fontSize}
              >
                <Box
                  $alignItems="center"
                  $justifyContent="space-between"
                  $marginBottom={16}
                >
                  <Heading20 marginBottom={0}>Activities</Heading20>
                  <SecondaryButton
                    type="button"
                    aria-label={`${
                      selectedPages.length === totalPages
                        ? 'Deselect'
                        : 'Select'
                    } all Teaching Notes`}
                    onClick={() => {
                      if (selectedPages.length === totalPages) {
                        handleDeselectAll();
                      } else {
                        handleSelectAll();
                      }
                    }}
                  >
                    {selectedPages.length === totalPages
                      ? 'Deselect All'
                      : 'Select All'}
                  </SecondaryButton>
                </Box>
                <BoxList $gap={16} role="group">
                  <Styled.TeacherDownloadItem>
                    <CheckableCard
                      content={
                        <ActivityPagePreview>
                          <ActivityAnswersPrintIntro {...printMetaData} />
                        </ActivityPagePreview>
                      }
                      title="Introduction"
                      ratio={ratio}
                      fieldName="pagesField"
                      value={INTRODUCTION_SECTION_ID}
                      id={INTRODUCTION_SECTION_ID}
                      checked={selectedPages.includes(INTRODUCTION_SECTION_ID)}
                      onChange={() => {
                        if (selectedPages.includes(INTRODUCTION_SECTION_ID)) {
                          setSelectedPages(
                            selectedPages.filter(
                              page => page !== INTRODUCTION_SECTION_ID
                            )
                          );
                        } else {
                          setSelectedPages([
                            ...selectedPages,
                            INTRODUCTION_SECTION_ID,
                          ]);
                        }
                      }}
                    />
                  </Styled.TeacherDownloadItem>
                  {classId &&
                    sectionsAndSubSectionsOverview?.map((page, index) => {
                      const details = getTitleDetails(page);
                      return (
                        <Styled.TeacherDownloadItem key={page.id}>
                          <CheckableCard
                            content={
                              <ActivityBuilderAnswersPagePreview
                                id={page.id}
                                key={`${page.id}-${language}`}
                                contentId={contentId}
                                contentType={contentType}
                                classId={classId}
                                sectionOverviews={sortedSectionsOverview}
                                version={version}
                                parentElement={null}
                                language={language}
                                headerFooterContent={{
                                  unitName: printMetaData.unitName,
                                  subjectName: printMetaData.subjectName,
                                  contentName: printMetaData.contentName,
                                  userName: printMetaData.userName || '',
                                  pageNumber: index + 1,
                                }}
                              />
                            }
                            title={<SimpleCardLabel {...details} />}
                            ratio={ratio}
                            fieldName="pagesField"
                            value={page.id}
                            id={page.id}
                            checked={selectedPages.includes(page.id)}
                            onChange={() => {
                              if (selectedPages.includes(page.id)) {
                                setSelectedPages(
                                  selectedPages.filter(p => p !== page.id)
                                );
                              } else {
                                setSelectedPages([...selectedPages, page.id]);
                              }
                            }}
                          />
                        </Styled.TeacherDownloadItem>
                      );
                    })}
                </BoxList>
              </ActivityBuilderThemeContextProvider>
            </SimpleModalContentInset>
          </Styled.TeacherDownloadContent>
          <SimpleModalActions>
            <Box $gap={16} $alignItems="center">
              <Feedback
                feature={FEEDBACK_ASSIGNMENTS_DOWNLOAD}
                linkText="Feedback about Download?"
              />
            </Box>

            <Box $gap={16} $alignItems="center">
              <SmallStyled marginBottom={0}>
                {`${selectedPages.length} ${pluralize(
                  'page',
                  selectedPages.length
                )} selected`}
              </SmallStyled>
              <PrimaryButton
                type="button"
                loading={isDownloading}
                disabled={isDownloading || noStudentsSelected}
                onClick={handleDownload}
                data-selector="modal-download-button"
              >
                Download
              </PrimaryButton>
            </Box>
          </SimpleModalActions>
        </SimpleModalContentOuter>
      )}
    </SimpleModal>
  );
};

export default TeacherDownloadStudentAnswers;
